import type { Driver, DriverHook, DriveStep } from 'driver.js';
import { repositionPopover } from './popover';
import {
  drawStage,
  getStepElement,
  getStepElementSelector,
  hideStage,
  isElementInViewport,
  removeStage,
  showStage,
} from './stage';
import type { TourStep } from './types';

const moveToChildren = (driver: Driver, steps: TourStep[]) => {
  driver.lastRootIndex = driver.getActiveIndex() ?? 0;
  const newSteps = steps.map((s) => {
    return {
      ...s,
      element: getStepElementSelector(s),
      disableActiveInteraction: !s.interaction,
    } satisfies DriveStep;
  });
  driver.setSteps(newSteps);
  driver.isDrivingThroughChildren = true;
  removeStage();
  driver.moveTo(0);
};

export const beforeHighlight: DriverHook = (_, step, { driver }) => {
  showStage();
  driver?.removeListeners();
  driver?.clearIntervals();

  const activeStep = step as TourStep;

  const element = getStepElement(activeStep) as HTMLElement;
  if (element && !isElementInViewport(element)) {
    element.scrollIntoView({ block: 'end' });
  }

  if (activeStep.children && !driver.isDrivingThroughChildren) {
    moveToChildren(driver, [activeStep, ...activeStep.children]);
  }

  if (element && !activeStep.allowScroll) {
    const scrollListener = (e: Event) => e.preventDefault();
    window.addEventListener('wheel', scrollListener, { passive: false });
    driver.addListener('wheel', window, scrollListener);
  }

  if (element && activeStep.interaction) {
    element.style.pointerEvents = 'auto';
  }
};

export const renderHighlight: DriverHook = (_, step, { config, driver }) => {
  const activeStep = step as TourStep;
  const element = getStepElement(activeStep);

  if (!activeStep || !element) {
    return;
  }

  repositionPopover(driver, activeStep);

  if (activeStep.interaction === 'click') {
    const moveNext = () => {
      config?.onNextClick?.(element, activeStep as DriveStep, {
        config,
        driver,
        state: driver.getState(),
      });
    };

    if (activeStep.interactionTarget) {
      element.addEventListener('click', hideStage);
      driver.addListener('click', element, hideStage);

      const intervalId = window.setInterval(() => {
        if (document.querySelector(`${activeStep.interactionTarget}`)) {
          moveNext();
          clearInterval(intervalId);
        }
      }, 300);

      driver.addInterval(intervalId);
    } else {
      element.addEventListener('click', moveNext);
      driver.addListener('click', element, moveNext);
    }
  }

  if (activeStep.stageSize) {
    drawStage(driver, activeStep?.stageSize);

    const redrawStage = () => {
      setTimeout(() => drawStage(driver, activeStep?.stageSize), 500);
    };

    window.addEventListener('resize', redrawStage);
    driver.addListener('resize', window, redrawStage);
  }
};

export const afterHighlight: DriverHook = (_, step) => {
  const activeStep = step as TourStep;
  const stepElement = getStepElement(activeStep);

  stepElement?.classList.remove('driver-active-element', 'driver-no-interaction');

  if (stepElement && activeStep.interaction && stepElement.style.pointerEvents) {
    stepElement.style.pointerEvents = '';
  }
};

export const destroyDriver = (driver: Driver) => {
  const activeStep = driver?.getActiveStep() as TourStep;
  const element = getStepElement(activeStep);

  if (element) {
    element.classList.remove('driver-active-element', 'driver-no-interaction');
  }

  if (element && activeStep?.interaction && element.style.pointerEvents) {
    element.style.pointerEvents = '';
  }

  driver.isDrivingThroughChildren = false;
  driver.lastRootIndex = 0;
  driver?.removeListeners?.();
  driver?.clearIntervals?.();
  driver?.setIsActive?.(false);

  removeStage();
  document.body.classList.remove('driver-active', 'driver-fade');
};
